import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Store, Plus, Trash2, Copy, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Member product manager — items sold on the public /store/:id page.
// Customer payments route to the member's Stripe account (minus platform fee).
export default function MyStoreCard() {
  const { user, getAuthHeader } = useAuth();
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({ name: '', description: '', price: '' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/connect/products`, { headers: getAuthHeader() });
      setProducts(res.data.products);
    } catch { /* card stays empty */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const create = async () => {
    const price = parseFloat(form.price);
    if (!form.name.trim() || !price || price <= 0) {
      toast.error('Give the product a name and a price');
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/connect/products`,
        { name: form.name.trim(), description: form.description.trim(), price },
        { headers: getAuthHeader() },
      );
      toast.success('Product listed');
      setForm({ name: '', description: '', price: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create product');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/connect/products/${id}`, { headers: getAuthHeader() });
      toast.success('Product removed');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not remove product');
    }
  };

  const storeUrl = `${window.location.origin}/store/${user?.id}`;

  return (
    <div className="wm-card p-6 rounded-lg" data-testid="my-store-card">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Store className="w-5 h-5 text-gold" /> My Store
          </h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-xl">
            Sell products directly to customers — payments go straight to your Stripe account
            (minus a 10% platform fee).
          </p>
        </div>
        <Button
          variant="outline"
          className="border-border text-xs"
          onClick={() => { navigator.clipboard.writeText(storeUrl); toast.success('Store link copied'); }}
          data-testid="copy-store-link-btn"
        >
          <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy store link
        </Button>
      </div>

      <div className="grid sm:grid-cols-[2fr,1fr] gap-3 mb-3">
        <div>
          <Label className="text-foreground">Product name</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="1-on-1 Strategy Call" className="mt-1 bg-input border-border text-foreground" data-testid="store-product-name" />
        </div>
        <div>
          <Label className="text-foreground">Price (USD)</Label>
          <Input type="number" min="0.5" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="99.00" className="mt-1 bg-input border-border text-foreground" data-testid="store-product-price" />
        </div>
      </div>
      <Label className="text-foreground">Description (optional)</Label>
      <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What the customer gets…" className="mt-1 bg-input border-border text-foreground" rows={2} data-testid="store-product-desc" />
      <Button onClick={create} disabled={saving} className="mt-3 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="store-product-create">
        {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Listing…</> : <><Plus className="w-4 h-4 mr-2" /> List product</>}
      </Button>

      {products.length > 0 && (
        <div className="mt-6 space-y-2" data-testid="store-product-list">
          {products.map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-3 bg-white/[0.03] border border-border rounded-lg px-4 py-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{p.name}</p>
                {p.description && <p className="text-xs text-muted-foreground truncate">{p.description}</p>}
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-gold font-semibold text-sm">${p.price.toFixed(2)}</span>
                <button onClick={() => remove(p.id)} className="text-red-400/70 hover:text-red-400 transition" data-testid={`store-product-delete-${p.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
